const AVATAR_COLORS = [
  "#4A5568", // slate
  "#2D3748", // navy
  "#5F7A61", // sage
  "#7B4B5A", // burgundy
  "#3D4551", // charcoal
  "#5C6B7A", // steel
  "#5D4E42", // espresso
  "#4C5C8A", // indigo
  "#4A6B5A", // forest
  "#6B5270", // plum
] as const;

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash);
}

/**
 * Deterministic color assignment based on user ID.
 * The same ID always produces the same color so a broker's monogram
 * looks consistent across directory, profile, and listing pages.
 */
export function getAvatarColor(userId: string): string {
  return AVATAR_COLORS[hashString(userId) % AVATAR_COLORS.length];
}

const PERSON_AVATAR_COLORS = [
  "#2563EB", // blue
  "#115E59", // teal
  "#166534", // green
  "#92400E", // amber
  "#DC2626", // red
  "#DB2777", // pink
  "#7C3AED", // violet
  "#4F46E5", // indigo
  "#0E7490", // cyan
  "#9A3412", // orange
  "#BE123C", // crimson
  "#6D28D9", // purple
  "#15803D", // forest
  "#C2410C", // rust
  "#1D4ED8", // royal
  "#0F766E", // dark teal
  "#A21CAF", // fuchsia
  "#B45309", // brown amber
] as const;

/**
 * Distinctive fallback color for OS person avatars (pipeline cards, lists,
 * deal header). Seed with a contact id so the same person stays the same
 * color, and neighboring leads stay easy to tell apart.
 */
export function getPersonAvatarColor(seed: string): string {
  const mixed = Math.imul(hashString(seed), 2654435761) >>> 0;
  return PERSON_AVATAR_COLORS[mixed % PERSON_AVATAR_COLORS.length]!;
}

function hexToRgb(hexColor: string): { r: number; g: number; b: number } {
  const hex = hexColor.replace("#", "");
  const normalized =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;

  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);

  return { r, g, b };
}

/**
 * Background glow for profile headers/cards.
 * Uses the same deterministic avatar color seed to keep visual identity consistent.
 */
export function getAvatarGlowBackground(userId: string): string {
  const baseColor = getAvatarColor(userId);
  const { r, g, b } = hexToRgb(baseColor);

  return [
    `radial-gradient(80% 58% at 8% 0%, rgba(${r}, ${g}, ${b}, 0.22) 0%, rgba(${r}, ${g}, ${b}, 0) 70%)`,
    `radial-gradient(70% 60% at 96% 6%, rgba(${r}, ${g}, ${b}, 0.12) 0%, rgba(${r}, ${g}, ${b}, 0) 72%)`,
    "linear-gradient(180deg, #FFFFFF 0%, #FBFCFD 100%)",
  ].join(", ");
}

/**
 * Seeded PRNG (linear congruential generator).
 * Returns a function that produces deterministic floats in [0, 1).
 */
function createSeededRandom(seed: number): () => number {
  let state = Math.abs(seed) || 1;
  return () => {
    state = (state * 1664525 + 1013904223) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

/**
 * Shift an RGB color by rotating hue slightly and adjusting saturation.
 * Amount is in degrees (-30..+30 is a subtle shift).
 */
function shiftColor(
  r: number,
  g: number,
  b: number,
  hueDeg: number,
  satMul: number,
): { r: number; g: number; b: number } {
  let rn = r / 255;
  let gn = g / 255;
  let bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (d > 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
    else if (max === gn) h = ((bn - rn) / d + 2) / 6;
    else h = ((rn - gn) / d + 4) / 6;
  }

  h = ((h * 360 + hueDeg) % 360) / 360;
  if (h < 0) h += 1;
  s = Math.min(1, Math.max(0, s * satMul));

  const hue2rgb = (p: number, q: number, t: number) => {
    const tt = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };

  if (s === 0) {
    rn = gn = bn = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    rn = hue2rgb(p, q, h + 1 / 3);
    gn = hue2rgb(p, q, h);
    bn = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(rn * 255),
    g: Math.round(gn * 255),
    b: Math.round(bn * 255),
  };
}

/**
 * Deterministic, subtle banner gradient for broker profile cards.
 * Seed with a broker identity string (prefer name) to get unique,
 * calm variations in position/size/opacity without noisy artifacts.
 */
export function getUniqueBannerGradient(seedKey: string): string {
  const seed = hashString(seedKey);
  const rand = createSeededRandom(seed);
  const baseColor = getAvatarColor(seedKey);
  const { r, g, b } = hexToRgb(baseColor);

  // Keep hue changes tight so the card stays neutral and clean.
  const primary = shiftColor(r, g, b, (rand() - 0.5) * 16, 0.9 + rand() * 0.15);
  const secondary = shiftColor(
    r,
    g,
    b,
    (rand() - 0.5) * 10,
    0.88 + rand() * 0.12,
  );

  // Subtle deterministic geometry.
  const leftX = 8 + Math.round(rand() * 12); // 8-20%
  const leftY = Math.round(rand() * 8); // 0-8%
  const leftW = 72 + Math.round(rand() * 16); // 72-88%
  const leftH = 48 + Math.round(rand() * 14); // 48-62%
  const leftOpacity = 0.11 + rand() * 0.05; // 0.11-0.16

  const rightX = 82 + Math.round(rand() * 14); // 82-96%
  const rightY = Math.round(rand() * 10); // 0-10%
  const rightW = 62 + Math.round(rand() * 14); // 62-76%
  const rightH = 46 + Math.round(rand() * 12); // 46-58%
  const rightOpacity = 0.06 + rand() * 0.04; // 0.06-0.10

  const baseAngle = 176 + Math.round(rand() * 8); // 176-184deg

  return [
    `radial-gradient(${leftW}% ${leftH}% at ${leftX}% ${leftY}%, rgba(${primary.r}, ${primary.g}, ${primary.b}, ${leftOpacity.toFixed(2)}) 0%, rgba(${primary.r}, ${primary.g}, ${primary.b}, 0) 72%)`,
    `radial-gradient(${rightW}% ${rightH}% at ${rightX}% ${rightY}%, rgba(${secondary.r}, ${secondary.g}, ${secondary.b}, ${rightOpacity.toFixed(2)}) 0%, rgba(${secondary.r}, ${secondary.g}, ${secondary.b}, 0) 74%)`,
    `linear-gradient(${baseAngle}deg, #FFFFFF 0%, #FBFCFD 100%)`,
  ].join(", ");
}

/**
 * Extract up to two initials from a full name string.
 * Handles single names, hyphenated names, and edge cases.
 */
export function getAvatarInitials(
  firstName?: string | null,
  lastName?: string | null,
): string {
  const first = firstName?.trim().charAt(0).toUpperCase() ?? "";
  const last = lastName?.trim().charAt(0).toUpperCase() ?? "";
  return first + last || "?";
}

/**
 * Extract initials from a combined name string (e.g. "Ryan Cave" → "RC").
 */
export function getAvatarInitialsFromName(
  name: string | null | undefined,
): string {
  if (!name || !name.trim()) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}
